import Campaign from '../store/Campaign';
import React from 'react';
import { CAMPAIGN_DISMISSED } from '../utils/eventNames';
import { CampaignEndSignal } from '../store/schema/CampaignEndSignal';
import { CampaignType } from '../store/schema/CampaignType';
import { CustomData } from 'mini-app-context';
import { endCampaign } from '../utils/endCampaign';
import { getDataLogger, LogOptions } from 'telemetry';
import { getModifiedViewProps } from './utils/getModifiedViewProps';
import { getOpxHostAppName } from 'mini-opx-config/lib/getOpxHostAppName';
import { LazySnackBar } from 'mini-snackbar';
import { startCampaign } from '../utils/startCampaign';

interface CustomLogParamsType {
    logData?: CustomData;
    logOptions?: LogOptions;
}

/**
 * This component renders the snack bar with default dismiss option on primary button click.
 */
export interface CampaignSnackBarConfig {
    message: string;
    actionText?: string;
    onPrimaryButtonClick?: () => void;
    customLogParams?: CustomLogParamsType;
}

export class CampaignSnackBar implements CampaignType<CampaignSnackBarConfig> {
    logger = getDataLogger();
    public start(campaign: Campaign<CampaignSnackBarConfig>) {
        startCampaign(campaign);
    }
    public end(campaign: Campaign<CampaignSnackBarConfig>, endSignal: CampaignEndSignal) {
        endCampaign(campaign, endSignal);
    }

    private logCampaignUsage(
        eventName: string,
        campaign: Campaign<CampaignSnackBarConfig>,
        customLogParams?: CustomLogParamsType
    ) {
        this.logger?.(
            eventName,
            {
                id: campaign.getRemoteCampaignId(),
                isUIAction: true,
                hostApp: getOpxHostAppName(),
                ...customLogParams?.logData,
            },
            { isCore: true, ...customLogParams?.logOptions }
        );
    }

    private logAndEndCampaign(
        eventName: string,
        campaign: Campaign<CampaignSnackBarConfig>,
        endSignal: CampaignEndSignal,
        customLogParams?: CustomLogParamsType
    ) {
        // Log usage DP
        this.logCampaignUsage(eventName, campaign, customLogParams);

        // End the campaign
        this.end(campaign, endSignal);
    }

    public render(
        campaign: Campaign<CampaignSnackBarConfig>,
        viewProps: CampaignSnackBarConfig
    ): JSX.Element {
        const self = this;
        const modifiedViewProps = getModifiedViewProps(viewProps);

        const onPrimaryButtonClick = () => {
            viewProps.onPrimaryButtonClick?.();
            self.logAndEndCampaign(
                CAMPAIGN_DISMISSED,
                campaign,
                CampaignEndSignal.OnClick,
                viewProps.customLogParams
            );
        };

        return (
            <LazySnackBar
                {...viewProps}
                {...modifiedViewProps}
                onPrimaryButtonClick={onPrimaryButtonClick}
            />
        );
    }
}
